import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type EnabledEmergencyCreditsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useEmergencyCreditsEducation";
import { CreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBar";
import { getCreditProgressBarLabel } from "@src/appV2/redesign/EmergencyCredits/utils/getCreditProgressBarLabel";

interface EarningCreditsStepProps {
  readonly emergencyCreditsConfig: EnabledEmergencyCreditsConfig;
}

export function EarningCreditsStep(props: EarningCreditsStepProps) {
  const { emergencyCreditsConfig } = props;
  const cancellationRequiredShifts = emergencyCreditsConfig.cancellation.requiredShiftsToEarnCredit;
  const latenessRequiredShifts = emergencyCreditsConfig.lateness.requiredShiftsToEarnCredit;
  const maxRequiredShifts = Math.max(cancellationRequiredShifts, latenessRequiredShifts);
  // This stage assumes `creditsToStartWith == 1` for both credit types.
  // If the feature flag is updated to grant more than 1 credit, it needs to be updated.
  return (
    <>
      <Text variant="h3" sx={{ fontWeight: 400, whiteSpace: "pre-line" }}>
        {`Earn back credits by\ncompleting ${maxRequiredShifts} shifts`}
      </Text>
      <Text sx={{ fontWeight: 400 }} color={(theme) => theme.palette.text.secondary}>
        You start with one credit for same-day cancellations, and one credit for late arrivals.
      </Text>
      <Stack spacing={2} sx={{ width: "100%", alignItems: "flex-start", marginTop: 1 }}>
        <Text sx={{ fontWeight: 400 }} color={(theme) => theme.palette.text.tertiary}>
          Examples
        </Text>
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: "16px",
            padding: theme.spacing(4),
            boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)",
            width: "100%",
          })}
        >
          <Stack spacing={4}>
            <Stack spacing={1}>
              <CreditProgressBar
                label={getCreditProgressBarLabel("cancellation", false)}
                currentCount={7}
                totalCount={cancellationRequiredShifts}
                color="blue"
              />
              <Text
                variant="caption"
                sx={{ fontWeight: 400, textAlign: "center" }}
                color={(theme) => theme.palette.text.tertiary}
              >
                {`7/${cancellationRequiredShifts} shifts complete`}
              </Text>
            </Stack>
            <Stack spacing={1}>
              <CreditProgressBar
                label={getCreditProgressBarLabel("lateness", false)}
                currentCount={5}
                totalCount={latenessRequiredShifts}
                color="green"
              />
              <Text
                variant="caption"
                sx={{ fontWeight: 400, textAlign: "center" }}
                color={(theme) => theme.palette.text.tertiary}
              >
                {`5/${latenessRequiredShifts} shifts complete`}
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </>
  );
}
